// TSL Activity Button Component
// Button to open the TSL Activity Log dialog

import { useState, useEffect } from 'react';
import { Activity, AlertCircle } from 'lucide-react';
import { TSLActivityLog } from './TSLActivityLog';
import { tslActivityService } from '../services/tslActivityService';
import type { TSLActivityStats } from '../types/tslActivity';

export function TSLActivityButton() {
  const [isLogOpen, setIsLogOpen] = useState(false);
  const [stats, setStats] = useState<TSLActivityStats | null>(null);
  const [hasRecentErrors, setHasRecentErrors] = useState(false);

  useEffect(() => {
    // Load initial stats
    updateStats();

    // Listen for activity updates
    const handleUpdate = () => {
      updateStats();
    };

    tslActivityService.addListener(handleUpdate);

    // Update stats every 30 seconds
    const interval = setInterval(updateStats, 30000);

    return () => {
      tslActivityService.removeListener(handleUpdate);
      clearInterval(interval);
    };
  }, []);

  const updateStats = () => {
    const activityStats = tslActivityService.getStats();
    setStats(activityStats);

    // Check for recent errors (last 30 minutes)
    const thirtyMinutesAgo = Date.now() - (30 * 60 * 1000);
    const recentEntries = tslActivityService.getFilteredEntries({
      dateFrom: thirtyMinutesAgo,
      success: false
    });

    setHasRecentErrors(recentEntries.length > 0);
  };

  const getButtonColor = () => {
    if (hasRecentErrors) {
      return 'bg-red-100 hover:bg-red-200 text-red-700 border-red-300';
    }

    if (stats && stats.lastActivity && (Date.now() - stats.lastActivity) < (1 * 60 * 1000)) {
      // Recent activity (last 5 minutes)
      return 'bg-green-100 hover:bg-green-200 text-green-700 border-green-300';
    }

    return 'bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300';
  };

  const getStatusText = () => {
    if (hasRecentErrors) {
      return 'TSL Errors';
    }

    if (stats && stats.lastActivity) {
      const minutesAgo = Math.floor((Date.now() - stats.lastActivity) / (60 * 1000));
      if (minutesAgo < 1) {
        return 'TSL Active';
      } else if (minutesAgo < 60) {
        return `${minutesAgo}m ago`;
      } else {
        const hoursAgo = Math.floor(minutesAgo / 60);
        return `${hoursAgo}h ago`;
      }
    }

    return 'TSL Log';
  };

  return (
    <>
      <button
        onClick={() => setIsLogOpen(true)}
        className={`flex items-center space-x-2 px-3 py-2 rounded-lg border transition-colors ${getButtonColor()}`}
        title="View TSL Activity Log"
      >
        {hasRecentErrors ? (
          <AlertCircle className="w-4 h-4" />
        ) : (
          <Activity className="w-4 h-4" />
        )}
        <span className="text-sm font-medium">{getStatusText()}</span>

        {stats && stats.totalEntries > 0 && (
          <span className="bg-white bg-opacity-70 px-2 py-1 rounded-full text-xs font-medium">
            {stats.totalEntries}
          </span>
        )}
      </button>

      <TSLActivityLog
        isOpen={isLogOpen}
        onClose={() => setIsLogOpen(false)}
      />
    </>
  );
}
